/**
 * `/auth/verify` — captain enters the 6-digit code; we POST it to
 * `/api/auth/verify` which sets the `nofuzz_session` cookie via
 * `Set-Cookie` and we redirect to `/setup`.
 *
 * Handle is preloaded from the `?handle=` query parameter (the success
 * state of `/auth/request` includes that link). We keep the field
 * editable in case a captain landed here via a stale link.
 */

import { useEffect, useState, type FormEvent } from "react";
import { Link, Navigate, useSearchParams } from "react-router-dom";
import { ApiError, apiPost } from "../lib/api";

type SubmitState =
  | { kind: "idle" }
  | { kind: "loading" }
  | { kind: "ok" }
  | { kind: "invalid" }
  | { kind: "error"; message: string };

export default function AuthVerify() {
  const [params] = useSearchParams();
  const initialHandle = params.get("handle") ?? "";
  const [handle, setHandle] = useState(initialHandle);
  const [code, setCode] = useState("");
  const [state, setState] = useState<SubmitState>({ kind: "idle" });

  useEffect(() => {
    // Keep the field in sync if the URL changes mid-session (rare but
    // possible if the captain pastes a new link into the same tab).
    setHandle(initialHandle);
  }, [initialHandle]);

  async function onSubmit(e: FormEvent<HTMLFormElement>) {
    e.preventDefault();
    const trimmedHandle = handle.trim();
    const trimmedCode = code.trim();
    if (trimmedHandle.length < 3 || !/^\d{6}$/.test(trimmedCode)) {
      setState({
        kind: "error",
        message: "Enter your handle and the 6-digit code from your email.",
      });
      return;
    }
    setState({ kind: "loading" });
    try {
      await apiPost<{ ok: boolean }>("/api/auth/verify", {
        handle: trimmedHandle,
        code: trimmedCode,
      });
      // Cookie is now set by the server; downstream pages will load
      // their session via /api/me.
      setState({ kind: "ok" });
    } catch (e) {
      if (e instanceof ApiError && e.status === 401) {
        setState({ kind: "invalid" });
      } else if (e instanceof ApiError) {
        setState({
          kind: "error",
          message: "We couldn't verify your code right now. Try again.",
        });
      } else {
        setState({
          kind: "error",
          message: "Network error. Check your connection and retry.",
        });
      }
    }
  }

  if (state.kind === "ok") {
    return <Navigate to="/setup" replace />;
  }

  return (
    <section>
      <h1>Enter your code</h1>
      <p>
        Paste the 6-digit code we just emailed to <code>{handle || "you"}</code>.
      </p>
      <form onSubmit={onSubmit} noValidate>
        <p>
          <label htmlFor="handle">
            <span>Email or iMessage handle</span>
          </label>
          <br />
          <input
            id="handle"
            name="handle"
            type="text"
            autoComplete="email"
            value={handle}
            onChange={(e) => setHandle(e.target.value)}
            disabled={state.kind === "loading"}
            style={{ width: "100%", maxWidth: 360, padding: "0.5rem" }}
          />
        </p>
        <p>
          <label htmlFor="code">
            <span>6-digit code</span>
          </label>
          <br />
          <input
            id="code"
            name="code"
            type="text"
            inputMode="numeric"
            autoComplete="one-time-code"
            pattern="\d{6}"
            maxLength={6}
            autoFocus
            value={code}
            onChange={(e) => setCode(e.target.value)}
            disabled={state.kind === "loading"}
            style={{
              width: "100%",
              maxWidth: 160,
              padding: "0.5rem",
              letterSpacing: "0.3em",
              fontFamily: "ui-monospace, monospace",
            }}
          />
        </p>
        <p>
          <button
            type="submit"
            className="button"
            disabled={state.kind === "loading"}
          >
            {state.kind === "loading" ? "Verifying…" : "Verify"}
          </button>
        </p>
        {state.kind === "invalid" && (
          <p role="alert" style={{ color: "#b3261e" }}>
            Invalid or expired code.{" "}
            <Link to="/auth/request">Request a new one.</Link>
          </p>
        )}
        {state.kind === "error" && (
          <p role="alert" style={{ color: "#b3261e" }}>
            {state.message}
          </p>
        )}
      </form>
    </section>
  );
}
